// src/services/customTopicService.ts

import apiClient from "./apiClient";
import { CustomTopicModel, CreateTopicSuggestionRequest, TopicTypeaheadResponse } from "../models/CustomTopicModel";
import { PageResponse } from "../models/PaginationModel";
import { TopicChannelCountModel } from "../models/CountryCountModel";

const BASE_URL = "/api/custom-topics";

/**
 * Custom Topic Service — public list/detail for the "Özel" topic type
 * plus the authenticated suggestion submission.
 */
export const customTopicService = {
  /**
   * Get all active custom topics with pagination.
   * Pass a categoryId to restrict the list to a single channel ("kanal") — left-pane filter.
   */
  async getAllTopics(page = 0, size = 20, sortBy = "recentEntryCount", sortDir = "desc", categoryId?: number | null, showRookieEntries = true): Promise<PageResponse<CustomTopicModel>> {
    const params = new URLSearchParams({
      page:    String(page),
      size:    String(size),
      sortBy,
      sortDir,
      // Viewer preference: when false the badge count (and the recentEntryCount
      // ordering) exclude rookie-authored entries, matching what the topic page shows.
      showRookieEntries: String(showRookieEntries),
    });
    if (categoryId != null) params.set("categoryId", String(categoryId));
    return apiClient.get<PageResponse<CustomTopicModel>>(`${BASE_URL}?${params.toString()}`);
  },

  /**
   * Active channels ("kanal") that have at least one active topic, with
   * per-channel counts — feeds the left-pane channel filter dropdown.
   */
  async getTopicChannels(): Promise<TopicChannelCountModel[]> {
    return apiClient.get<TopicChannelCountModel[]>(`${BASE_URL}/channels`);
  },

  /**
   * Get topics whose PRIMARY channel is one of the two reserved system
   * channels — feeds the İstikram and Bırak domain tabs. No categoryId param:
   * these two tabs don't have their own kanal sub-filter.
   */
  async getSystemChannelTopics(
    systemKey: "istikram" | "birak",
    page = 0,
    size = 20,
    sortBy = "recentEntryCount",
    sortDir = "desc",
    showRookieEntries = true
  ): Promise<PageResponse<CustomTopicModel>> {
    const params = new URLSearchParams({
      page:    String(page),
      size:    String(size),
      sortBy,
      sortDir,
      showRookieEntries: String(showRookieEntries),
    });
    return apiClient.get<PageResponse<CustomTopicModel>>(`${BASE_URL}/system/${systemKey}?${params.toString()}`);
  },

  /**
   * Random slice of active topics — backs the left-pane "Rastgele" (shuffle)
   * button. Always a single page: the backend returns totalPages = 1 so the
   * "daha da..." button stays hidden on a shuffled list.
   */
  async getRandomTopics(size = 20, categoryId?: number | null, showRookieEntries = true): Promise<PageResponse<CustomTopicModel>> {
    const params = new URLSearchParams({
      size: String(size),
      showRookieEntries: String(showRookieEntries),
    });
    if (categoryId != null) params.set("categoryId", String(categoryId));
    return apiClient.get<PageResponse<CustomTopicModel>>(`${BASE_URL}/random?${params.toString()}`);
  },

  /**
   * Random slice of topics whose PRIMARY channel is one of the reserved system
   * channels — the İstikram/Bırak equivalent of getRandomTopics.
   */
  async getRandomSystemChannelTopics(
    systemKey: "istikram" | "birak",
    size = 20,
    showRookieEntries = true
  ): Promise<PageResponse<CustomTopicModel>> {
    const params = new URLSearchParams({
      size: String(size),
      showRookieEntries: String(showRookieEntries),
    });
    return apiClient.get<PageResponse<CustomTopicModel>>(`${BASE_URL}/system/${systemKey}/random?${params.toString()}`);
  },

  /**
   * Get topic by database ID.
   */
  async getTopicById(id: number, showRookieEntries = true): Promise<CustomTopicModel> {
    return apiClient.get<CustomTopicModel>(`${BASE_URL}/${id}?showRookieEntries=${showRookieEntries}`);
  },

  /**
   * Get topic by SEO-friendly slug — used by the /gundem/{slug} route.
   */
  async getTopicBySlug(slug: string, showRookieEntries = true): Promise<CustomTopicModel> {
    return apiClient.get<CustomTopicModel>(`${BASE_URL}/slug/${slug}?showRookieEntries=${showRookieEntries}`);
  },

  /**
   * Submit a new topic suggestion (authenticated users only).
   * The suggestion lands in the admin panel for review.
   */
  /**
   * Title typeahead for the suggest modal — fires from the 3rd character.
   * Returns up to 8 matching titles plus an exact-duplicate flag.
   */
  async typeahead(q: string): Promise<TopicTypeaheadResponse> {
    const params = new URLSearchParams({ q });
    return apiClient.get<TopicTypeaheadResponse>(`${BASE_URL}/typeahead?${params.toString()}`);
  },

  async suggestTopic(request: CreateTopicSuggestionRequest): Promise<void> {
    await apiClient.post<void>(`${BASE_URL}/suggestions`, request);
  },
};

export default customTopicService;